import {
    QueryClient,
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryResult
} from '@tanstack/react-query';

import AccountService from 'src/services/account.service';
import { AccountModel } from 'src/types/account.model';
import { invalidateBudgets } from 'src/hooks/queries/budgets';
import { invalidateReviewTransactions } from 'src/hooks/queries/transactions';
import { AccountSyncStatusModel } from 'src/types/account-sync-status.model';
import { invalidateTransactionsSpending } from 'src/hooks/queries/spending';

enum QueryTypesEnum {
    visible = 'visible',
    hidden = 'hidden'
}

const queryKey = 'accounts';
const syncStatusKey = 'syncStatus';

export function useVisibleAccounts(): UseQueryResult<AccountModel[]> {
    return useQuery<AccountModel[]>({
        queryKey: [queryKey, { accountsType: QueryTypesEnum.visible }],
        queryFn: async () => await AccountService.getAccounts(),
        keepPreviousData: true
    });
}

export function useHiddenAccounts(): UseQueryResult<AccountModel[]> {
    return useQuery<AccountModel[]>({
        queryKey: [queryKey, { accountsType: QueryTypesEnum.hidden }],
        queryFn: async () => {
            const data = await AccountService.hiddenAccounts();
            return data.map((account: AccountModel) => {
                account.hidden = true;
                return account;
            });
        },
        keepPreviousData: true
    });
}

const getVisibleAccountsQueryData = (queryClient: QueryClient): AccountModel[] => {
    return queryClient.getQueryData([queryKey, { accountsType: QueryTypesEnum.visible }]);
};
const getHiddenAccountsQueryData = (queryClient: QueryClient): AccountModel[] => {
    return queryClient.getQueryData([queryKey, { accountsType: QueryTypesEnum.hidden }]);
};
const setVisibleAccountsQueryData = (queryClient: QueryClient, data: AccountModel[]): void => {
    queryClient.setQueryData([queryKey, { accountsType: QueryTypesEnum.visible }], data);
};
const setHiddenAccountsQueryData = (queryClient: QueryClient, data: AccountModel[]): void => {
    queryClient.setQueryData([queryKey, { accountsType: QueryTypesEnum.hidden }], data);
};

export const useShowAccount = (): UseMutationResult<number, unknown, string> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (accountId: string) => await AccountService.showAccount(accountId),
        onMutate: async (accountIdToShow: string) => {
            await queryClient.cancelQueries([queryKey]);
            const previousHiddenAccounts: AccountModel[] = getHiddenAccountsQueryData(queryClient);
            const previousVisibleAccounts: AccountModel[] = getVisibleAccountsQueryData(queryClient);
            setHiddenAccountsQueryData(
                queryClient,
                previousHiddenAccounts?.filter((a) => a.accountId !== accountIdToShow)
            );
            const updatedAccount =
                previousHiddenAccounts?.find((a) => a.accountId === accountIdToShow) ?? ({} as AccountModel);
            updatedAccount.hidden = false;
            setVisibleAccountsQueryData(queryClient, [...(previousVisibleAccounts ?? []), updatedAccount]);
            return { previousHiddenAccounts, previousVisibleAccounts };
        },
        onError: (e, accountIdToShow, context) => {
            setHiddenAccountsQueryData(queryClient, context?.previousHiddenAccounts);
            setVisibleAccountsQueryData(queryClient, context?.previousVisibleAccounts);
        },
        onSuccess: async (): Promise<void> => {
            await invalidateAllAccountRelatedQueries(queryClient);
        }
    });
};

export const useHideAccount = (): UseMutationResult<number, unknown, number> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => await AccountService.hideAccount(id),
        onMutate: async (accountIdToHide: number) => {
            await queryClient.cancelQueries([queryKey]);
            const previousHiddenAccounts: AccountModel[] = getHiddenAccountsQueryData(queryClient);
            const previousVisibleAccounts: AccountModel[] = getVisibleAccountsQueryData(queryClient);
            setVisibleAccountsQueryData(queryClient, previousVisibleAccounts?.filter((a) => a.id !== accountIdToHide));
            const updatedAccount =
                previousVisibleAccounts?.find((a) => a.id === accountIdToHide) ?? ({} as AccountModel);
            updatedAccount.hidden = true;
            setHiddenAccountsQueryData(queryClient, [...(previousHiddenAccounts ?? []), updatedAccount]);
            return { previousHiddenAccounts, previousVisibleAccounts };
        },
        onError: (e, accountIdToHide, context) => {
            setHiddenAccountsQueryData(queryClient, context?.previousHiddenAccounts);
            setVisibleAccountsQueryData(queryClient, context?.previousVisibleAccounts);
        },
        onSuccess: async (): Promise<void> => {
            await invalidateAllAccountRelatedQueries(queryClient);
        }
    });
};

export const invalidateAccounts = async (queryClient: QueryClient): Promise<void> => {
    await queryClient.invalidateQueries([queryKey]);
};

export const invalidateSyncStatus = async (queryClient: QueryClient): Promise<void> => {
    await queryClient.invalidateQueries([syncStatusKey]);
};

export function useAllAccounts(): {
    data: AccountModel[];
    isLoading: boolean;
} {
    const { data: visibleAccounts, isLoading: isLoadingVisibleAccounts } = useVisibleAccounts();
    const { data: hiddenAccounts, isLoading: isLoadingHiddenAccounts } = useHiddenAccounts();
    const concatenatedAccounts: AccountModel[] = visibleAccounts?.concat(hiddenAccounts);
    return {
        data: concatenatedAccounts,
        isLoading: isLoadingVisibleAccounts || isLoadingHiddenAccounts
    };
}

export const useAccountSyncStatuses = (enabled: boolean = true): UseQueryResult<AccountSyncStatusModel[]> => {
    return useQuery<AccountSyncStatusModel[]>({
        queryKey: [syncStatusKey],
        queryFn: async () => await AccountService.getAccountSyncStatuses(),
        enabled
    });
};

export const useResyncAccount = (): UseMutationResult<void, unknown, number> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => await AccountService.resyncAccount(id),
        onSuccess: (): void => {
            void invalidateSyncStatus(queryClient);
        }
    });
};

export const invalidateAllAccountRelatedQueries = async (queryClient: QueryClient): Promise<void> => {
    void invalidateBudgets(queryClient);
    void invalidateTransactionsSpending(queryClient);
    void invalidateReviewTransactions(queryClient);
    void invalidateSyncStatus(queryClient);
    return await invalidateAccounts(queryClient);
};
